// Accountant-side per-client saved-card API client.
// Talks to the accountant-only routes under /api/CreditTerminal/accountant/*.
// Used by the Collection / MASAV "Manage saved cards" modal and by the
// MASAV bulk-receipt credit-card payment flow.

import config from '../config';
import selectedEnvironment from '../env';

const BASE = config[selectedEnvironment].API_URL;
const ROOT = `${BASE}/CreditTerminal/accountant`;

const getCurrentToken = async () => {
    const raw = localStorage.getItem('token');
    return raw ? JSON.parse(raw) : null;
};
const jsonHeaders = async () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${await getCurrentToken()}`,
});

const guard = async (res) => {
    if (!res.ok) {
        const txt = await res.text();
        let msg;
        try { msg = JSON.parse(txt).message || JSON.parse(txt).error || txt; }
        catch { msg = txt; }
        throw new Error(msg);
    }
    const ct = res.headers.get('content-type');
    if (ct?.includes('application/json')) return res.json();
    return null;
};

const get = async (url) => guard(await fetch(url, { headers: await jsonHeaders() }));
const post = async (url, body) => guard(await fetch(url, { method: 'POST', headers: await jsonHeaders(), body: JSON.stringify(body) }));
const patch = async (url, body) => guard(await fetch(url, { method: 'PATCH', headers: await jsonHeaders(), body: JSON.stringify(body) }));
const del = async (url) => guard(await fetch(url, { method: 'DELETE', headers: await jsonHeaders() }));

const newIdempotencyKey = () =>
    'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });

const clientSavedCardsService = {
    newIdempotencyKey,

    /**
     * Lists clients + their (optional) active saved card.
     * @param {object} params
     * @param {boolean} params.leadingBusiness  true = all clients under accountant, false = single client.
     * @param {string=}  params.clientCaseId   when not leadingBusiness, the client CaseId.
     */
    list: ({ leadingBusiness = false, clientCaseId } = {}) => {
        const q = new URLSearchParams();
        q.set('leadingBusiness', leadingBusiness ? 'true' : 'false');
        if (clientCaseId) q.set('clientCaseId', clientCaseId);
        return get(`${ROOT}/saved-cards?${q.toString()}`);
    },

    /**
     * Tokenizes a card via Tranzila verify and persists it as the client's
     * one active saved card.
     */
    save: ({ clientCaseId, cardNumber, expiryMonth, expiryYear, cvv, cardHolderId }) =>
        post(`${ROOT}/saved-cards`, {
            clientCaseId,
            cardNumber,
            expiryMonth: Number(expiryMonth),
            expiryYear: Number(expiryYear),
            cvv,
            cardHolderId: cardHolderId || null,
        }),

    /** Soft-deletes the saved card. */
    remove: (savedCardId) => del(`${ROOT}/saved-cards/${savedCardId}`),

    /**
     * Updates only the CVV on an existing saved card. Used to recover from
     * Tranzila response code 006 ("ת.ז. או CVV שגויים") without re-tokenising
     * the card. Pass an empty/null cvv to clear it.
     */
    updateCvv: (savedCardId, cvv) =>
        patch(`${ROOT}/saved-cards/${savedCardId}/cvv`, { cvv: cvv || null }),

    /**
     * Charges the client's saved card. On success the server records a
     * CreditCard payment on the client's ledger via LedgerBridge.
     */
    charge: ({
        clientCaseId, savedCardId, amount, description, reference, paymentDate,
        paymentPlan, installmentsNumber,
        idempotencyKey,
    }) => post(`${ROOT}/charge-saved-card`, {
        idempotencyKey: idempotencyKey || newIdempotencyKey(),
        clientCaseId,
        savedCardId,
        amount: Number(amount),
        description: description || null,
        reference: reference || null,
        paymentDate: paymentDate || null,
        paymentPlan: paymentPlan ?? null,
        installmentsNumber: installmentsNumber ?? null,
    }),
};

export default clientSavedCardsService;
